import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useProducts, useCategories, useCategoryTree } from '../hooks/useQueries';
import ProductCard from '../components/ProductCard';
import { trackEvent } from '../utils/analytics';
import './ProductList.css';

const SORT_OPTIONS = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'discount', label: 'Biggest Discount' },
  { value: 'newest', label: 'New Arrivals' },
];

const ProductSkeleton = () => (
  <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e8e0d6', overflow: 'hidden' }}>
    <div style={{ width: '100%', aspectRatio: '4/5', background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite' }} />
    <div style={{ padding: 16 }}>
      <div style={{ height: 12, width: '60%', background: '#f0f0f0', borderRadius: 4, marginBottom: 8 }} />
      <div style={{ height: 12, width: '80%', background: '#f0f0f0', borderRadius: 4, marginBottom: 8 }} />
    </div>
  </div>
);

const ProductList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState('featured');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [filterOpen, setFilterOpen] = useState(false);

  const activeCategory = searchParams.get('category') || '';
  const searchQuery = searchParams.get('search') || '';
  const filterType = searchParams.get('filter') || ''; // 'featured' | 'new'

  // API data
  const { data: allProducts = [], isLoading: productsLoading, isError } = useProducts();
  const { data: categories = [], isLoading: catsLoading } = useCategories();
  const { data: categoryTree = [] } = useCategoryTree();

  // Which main-category nodes are expanded in the Occasion filter tree
  const [openFilterCats, setOpenFilterCats] = useState(() => new Set());

  // Auto-expand the branch leading to whichever category is currently selected
  useEffect(() => {
    if (!activeCategory || categoryTree.length === 0) return;
    const ancestors = [];
    const findPath = (nodes, path) => {
      for (const node of nodes) {
        if (node.slug === activeCategory) { ancestors.push(...path); return true; }
        if (node.children?.length && findPath(node.children, [...path, node.id])) return true;
      }
      return false;
    };
    findPath(categoryTree, []);
    if (ancestors.length) {
      setOpenFilterCats((prev) => new Set([...prev, ...ancestors]));
    }
  }, [activeCategory, categoryTree]);

  useEffect(() => {
    if (searchQuery) {
      trackEvent('SEARCH', { search_keyword: searchQuery });
    } else if (activeCategory) {
      const cat = categories.find((c) => c.slug === activeCategory);
      trackEvent('CATEGORY_VIEW', { category_id: cat?.rawId ?? null, metadata: { slug: activeCategory } });
    }
  }, [activeCategory, searchQuery]);

  const toggleFilterCat = (id) => {
    setOpenFilterCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Recursively renders a category node and its children as an indented,
  // expandable tree so main categories and nested subcategories are both filterable.
  const renderCategoryFilter = (node, depth = 0) => {
    const hasChildren = node.children?.length > 0;
    const isOpen = openFilterCats.has(node.id);
    const isActive = activeCategory === node.slug;
    return (
      <div key={node.id} className="filter-cat-node">
        <div className="filter-cat-row" style={{ paddingLeft: depth * 14 }}>
          <button
            className={`filter-cat-btn ${isActive ? 'active' : ''} ${depth > 0 ? 'filter-cat-btn--nested' : ''}`}
            onClick={() => setCategory(node.slug)}
          >
            <span>{node.name}</span>
            <div></div>
          </button>
          {hasChildren && (
            <button
              type="button"
              className="filter-cat-toggle"
              aria-label={`Toggle ${node.name}`}
              aria-expanded={isOpen}
              onClick={() => toggleFilterCat(node.id)}
            >
              <svg
                className={isOpen ? 'filter-cat-toggle-icon rotated' : 'filter-cat-toggle-icon'}
                width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          )}
        </div>
        {hasChildren && isOpen && (
          <div className="filter-cat-children">
            {node.children.map((child) => renderCategoryFilter(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const setCategory = (cat) => {
    const params = new URLSearchParams(searchParams);
    if (cat) params.set('category', cat); else params.delete('category');
    params.delete('search');
    params.delete('filter');
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    let list = [...allProducts]; // already active-only from hook

    // Filter by type (featured / new arrivals)
    if (filterType === 'featured') list = list.filter((p) => p.isFeatured);
    if (filterType === 'new') list = list.filter((p) => p.isNewArrival);

    // Filter by category slug
    if (activeCategory) {
      list = list.filter((p) => p.categorySlugs.includes(activeCategory));
    }

    // Filter by search query — matches if the product contains ANY of the
    // search words, not just the whole typed phrase as one substring. So
    // "means wallet" splits into ["means", "wallet"], and any product with
    // "wallet" in its name/description/category/SKU shows up, even though
    // "means wallet" itself never appears anywhere. Also does a light
    // singular/plural match ("wallet" ⇄ "wallets").
    //
    // Matching is done word-by-word (not raw substring) so a short stemmed
    // word like "men" (from "mens") can't accidentally match inside an
    // unrelated word like "compartMENt" — it has to appear as its own word.
    if (searchQuery) {
      const STOPWORDS = new Set(['a', 'an', 'the', 'and', 'or', 'for', 'of', 'in', 'on', 'to', 'with', 'is']);
      const normalizeSku = (s) => (s || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
      const stem = (w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w);
      const toWords = (s) => (s || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);

      const words = toWords(searchQuery).filter((w) => w.length > 1 && !STOPWORDS.has(w));
      // If every word was a stopword (e.g. searching just "the"), fall back
      // to the raw query so search still does something reasonable.
      const searchTokens = words.length > 0 ? words : toWords(searchQuery);

      list = list.filter((p) => {
        const haystackWords = toWords(`${p.name} ${p.description || ''} ${p.categoryName}`);
        const haystackStems = new Set(haystackWords.map(stem));
        const skuHaystack = normalizeSku(p.sku);

        const nameOrDescMatch = searchTokens.some((t) => haystackStems.has(stem(t)));
        const skuMatch = searchTokens.some((t) => {
          const skuToken = normalizeSku(t);
          return skuToken.length > 0 && skuHaystack.includes(skuToken);
        });
        return nameOrDescMatch || skuMatch;
      });
    }

    // Filter by price range (using sale price)
    list = list.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Sort
    switch (sort) {
      case 'price-asc': return list.sort((a, b) => a.price - b.price);
      case 'price-desc': return list.sort((a, b) => b.price - a.price);
      case 'rating': return list.sort((a, b) => b.rating - a.rating);
      case 'discount': return list.sort((a, b) => b.discount - a.discount);
      case 'newest': return list.sort((a, b) => (b.isNewArrival ? 1 : 0) - (a.isNewArrival ? 1 : 0));
      default: return list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    }
  }, [allProducts, activeCategory, searchQuery, priceRange, sort, filterType]);

  const activeCatData = categories.find((c) => c.slug === activeCategory);

  return (
    <div className="product-list-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-bar">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <span>›</span>
            <Link to="/products">All Gifts</Link>
            {activeCatData && <><span>›</span><span>{activeCatData.name}</span></>}
            {searchQuery && <><span>›</span><span>Search: "{searchQuery}"</span></>}
            {filterType === 'featured' && <><span>›</span><span>Bestsellers</span></>}
            {filterType === 'new' && <><span>›</span><span>New Arrivals</span></>}
          </div>
        </div>
      </div>

      <div className="container">
        <div className="plp-layout">
          {/* Sidebar */}
          <aside className={`plp-sidebar ${filterOpen ? 'plp-sidebar--open' : ''}`}>
            <div className="sidebar-header">
              <h3>Filters</h3>
              <button className="sidebar-close" onClick={() => setFilterOpen(false)}>✕</button>
            </div>

            {/* Categories from API */}
            <div className="filter-group">
              <h4 className="filter-title">Occasion</h4>
              <button
                className={`filter-cat-btn ${!activeCategory ? 'active' : ''}`}
                onClick={() => setCategory('')}
              >
                All Gifts
              </button>

              {catsLoading ? (
                <p style={{ fontSize: 13, color: '#aaa', padding: '8px 0' }}>Loading…</p>
              ) : (
                categoryTree.map((cat) => renderCategoryFilter(cat))
              )}
            </div>

            {/* Quick filters */}
            <div className="filter-group">
              <h4 className="filter-title">Quick Filter</h4>
              <button
                className={`filter-cat-btn ${filterType === 'featured' ? 'active' : ''}`}
                onClick={() => { setSearchParams({ filter: 'featured' }); }}
              >⭐ Bestsellers</button>
              <button
                className={`filter-cat-btn ${filterType === 'new' ? 'active' : ''}`}
                onClick={() => { setSearchParams({ filter: 'new' }); }}
              >✨ New Arrivals</button>
            </div>

            {/* Price Range */}
            <div className="filter-group">
              <h4 className="filter-title">Price Range (₹)</h4>
              <div className="price-range">
                <div className="price-inputs">
                  <div className="price-input-group">
                    <label>Min</label>
                    <input
                      type="number"
                      value={priceRange[0]}
                      min={0} max={priceRange[1]}
                      onChange={(e) => setPriceRange([+e.target.value, priceRange[1]])}
                    />
                  </div>
                  <span>–</span>
                  <div className="price-input-group">
                    <label>Max</label>
                    <input
                      type="number"
                      value={priceRange[1]}
                      min={priceRange[0]} max={50000}
                      onChange={(e) => setPriceRange([priceRange[0], +e.target.value])}
                    />
                  </div>
                </div>
              </div>
            </div>

            <button className="btn btn-primary-warm" style={{ width: '100%', marginTop: 8 }} onClick={() => setFilterOpen(false)}>
              Apply Filters
            </button>
          </aside>

          {/* Main */}
          <main className="plp-main">
            {/* Top bar */}
            <div className="plp-topbar">
              <div className="plp-topbar__left">
                <button className="filter-toggle-btn" onClick={() => setFilterOpen(true)}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                  Filters
                </button>
                <p className="plp-count">
                  {productsLoading ? 'Loading…' : `${filtered.length} gifts`}
                </p>
              </div>
              <div className="plp-topbar__right">
                <label className="sort-label">Sort by:</label>
                <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category pills */}
            {/* <div className="category-pills">
              <button
                className={`category-pill ${!activeCategory && !filterType ? 'active' : ''}`}
                onClick={() => setSearchParams({})}
              >All</button>
              <button
                className={`category-pill ${filterType === 'featured' ? 'active' : ''}`}
                onClick={() => setSearchParams({ filter: 'featured' })}
              >⭐ Bestsellers</button>
              <button
                className={`category-pill ${filterType === 'new' ? 'active' : ''}`}
                onClick={() => setSearchParams({ filter: 'new' })}
              >✨ New Arrivals</button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  className={`category-pill ${activeCategory === cat.slug ? 'active' : ''}`}
                  onClick={() => setCategory(cat.slug)}
                >
                  {cat.name}
                </button>
              ))}
            </div> */}

            {/* Error state */}
            {isError && (
              <div style={{ padding: 24, background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 12, color: '#dc2626', marginBottom: 24 }}>
                ⚠️ Failed to load products. Please ensure the API server is running at <strong>http://localhost:5000</strong>
              </div>
            )}

            {/* Grid */}
            {productsLoading ? (
              <div className="plp-grid">
                {[1, 2, 3, 4, 5, 6].map((n) => <ProductSkeleton key={n} />)}
              </div>
            ) : filtered.length > 0 ? (
              <div className="plp-grid">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} delay={i * 40} />)}
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">🎁</div>
                <h3>No gifts found</h3>
                <p>Try adjusting your filters or search terms</p>
                <button className="btn btn-primary" onClick={() => setSearchParams({})}>
                  Clear Filters
                </button>
              </div>
            )}
          </main>
        </div>
      </div>

      {filterOpen && <div className="filter-backdrop" onClick={() => setFilterOpen(false)} />}
    </div>
  );
};

export default ProductList;